import React, { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/router"
import { useFormik } from "formik"
import { signIn } from "next-auth/react"
import { useTranslation } from "next-i18next"
import { getQueryPersistedHref } from "utils"

import type { ISignInServiceResponse } from "types/next-auth"
import { AuthFormLink } from "components/Layouts/auth"
import SocialProviders from "components/SocialProviders"
import {
  Alert,
  Box,
  Button,
  Divider,
  Input,
  Text,
} from "meditopia-ui/components"
import { useAuthStore } from "store/auth-store"

import { SigninSchema } from "./validation"

const SigninForm = () => {
  const { t } = useTranslation("auth")
  const { setAuthenticated } = useAuthStore()
  const router = useRouter()
  const emailRef = useRef<HTMLInputElement>(null)
  const passwordRef = useRef<HTMLInputElement>(null)
  const [serviceError, setServiceError] = useState("")
  const {
    values,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
    setValues,
  } = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: SigninSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setServiceError("")
      setSubmitting(true)
      // @ts-ignore
      const response: ISignInServiceResponse = await signIn("credentials", {
        username: values.email,
        password: values.password,
        redirect: false,
      })

      if (response.error) {
        setServiceError(response.error)
        setSubmitting(false)
        return false
      }

      setAuthenticated(true)
      setSubmitting(false)
    },
  })

  useEffect(() => {
    const browserEmailValue = emailRef.current?.value
    const browserPasswordValue = passwordRef.current?.value

    if (browserEmailValue || browserPasswordValue) {
      setValues({
        email: browserEmailValue || "",
        password: browserPasswordValue || "",
      })
    }
  }, [setValues])

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Alert error={serviceError} />
      <Box mb="6">
        <Input
          ref={emailRef}
          type="email"
          name="email"
          placeholder={t("fields.email")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.email}
          error={
            errors.email && touched.email
              ? t(`errors.email.${errors.email}`)
              : ""
          }
          required
        />
      </Box>

      <Box mb="4">
        <Input
          ref={passwordRef}
          type="password"
          name="password"
          placeholder={t("fields.password")}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.password}
          error={
            errors.password && touched.password
              ? t(`errors.password.${errors.password}`)
              : ""
          }
          required
        />
      </Box>

      <Box mb={["6", "8"]}>
        <Link
          href={getQueryPersistedHref({
            href: "/forgotPassword/",
            keys: ["callbackUrl", "forcedTheme", "slackUserId", "slackTeamId"],
            query: router.query,
          })}
          passHref
        >
          <a>
            <AuthFormLink>{t("forgotPassword")}</AuthFormLink>
          </a>
        </Link>
      </Box>

      <Button
        type="submit"
        disabled={isSubmitting}
        loading={isSubmitting}
        colorScheme="secondary"
        _light={{ colorScheme: "primary" }}
        mb="6"
        width="100%"
      >
        {t("mainActions.signin")}
      </Button>

      <Box display="flex" flexDirection="row" alignItems="center" mb="8">
        <Text
          mr="2"
          variant="body-small-regular"
          color="white.100"
          _light={{ color: "gray.600" }}
        >
          {t("noUserYet.text")}
        </Text>
        <Link
          href={getQueryPersistedHref({
            href: "/signUp/",
            keys: ["callbackUrl", "forcedTheme", "slackUserId", "slackTeamId"],
            query: router.query,
          })}
          passHref
        >
          <a>
            <AuthFormLink>{t("noUserYet.action")}</AuthFormLink>
          </a>
        </Link>
      </Box>

      <Divider color="white.16" _light={{ color: "gray.0" }}>
        <Text
          variant="heading-medium"
          color="white.100"
          _light={{ color: "gray.600" }}
        >
          {t("or.signin")}
        </Text>
      </Divider>

      <Box mt="6">
        <SocialProviders />
      </Box>

      <Button
        size="small"
        variant="ghost"
        color="white.100"
        width="100%"
        border="1px solid"
        borderColor="white.16"
        backgroundColor="black.08"
        _light={{
          color: "black.80",
          borderColor: "gray.100",
          backgroundColor: "transparent",
        }}
        mt="6"
        onClick={() =>
          (window.location.href = `https://ssogate.meditopia.com/sso/web?lang=${router.locale}`)
        }
      >
        {t("ssoText")}
      </Button>
    </form>
  )
}

export default SigninForm
